import { Button, Modal, ScrollArea, useToast } from '@ethsign/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useRef, useState } from 'react';

import {
  AddIcon,
  Card,
  ColorRemoveIcon,
  ImageUploadWithCropper,
  SignatureCanvas,
  SignatureStyleTypeEnum,
  SignInputTab,
  SignPreview,
  Table,
  Tabs
} from '@/components';
import { Spin } from '@/components/Loading';
import { SignatureCanvasRef } from '@/components/SignatureCanvas';
import { useUserInfo } from '@/hooks';
import { createSignatureStyle, deleteSignatureStyle, fetchSignatureStyles } from '@/services';
import { dateFormat, textToBase64 } from '@/utils';

const AddOrEditSignatureModal = ({ onSuccess }: { onSuccess: () => void }) => {
  const { user } = useUserInfo();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [textSign, setTextSign] = useState(user?.nickName || '');
  const [loading, setLoading] = useState(false);
  const [activityKey, setActivityKey] = useState(SignatureStyleTypeEnum.TYPE);
  const ref = useRef<SignatureCanvasRef>(null);
  const [isBegin, setIsBegin] = useState(false);
  const copperRef = useRef<{ getImage: () => string }>(null);
  const { mutateAsync } = useMutation({
    mutationFn: createSignatureStyle,
    mutationKey: ['createSignatureStyle']
  });

  const onClose = () => {
    setOpen(false);
    setTextSign('');
  };

  const handleConfirm = async () => {
    console.log('confirm');
    setLoading(true);
    try {
      let image = '';
      let errorMessage = '';
      if (activityKey === SignatureStyleTypeEnum.DRAW) {
        if (ref.current?.isEmpty()) {
          errorMessage = 'Please sign your name';
        }
        image = ref.current?.getTrimmedCanvas()?.toDataURL() as string;
      } else if (activityKey === SignatureStyleTypeEnum.TYPE) {
        if (!textSign) {
          errorMessage = 'Please enter your name';
        }
        image = textToBase64(textSign);
      } else {
        const uploadSign = copperRef.current?.getImage() as string;
        if (!uploadSign) {
          errorMessage = 'Please upload your signature';
        }
        image = uploadSign;
      }
      if (errorMessage) {
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'error'
        });
        return;
      }
      await mutateAsync({
        image
      });
      toast({
        title: 'Success',
        description: 'Signature style created',
        variant: 'success',
        duration: 1000
      });
      onSuccess();
      onClose();
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Modal
        open={open}
        onOpenChange={setOpen}
        header={
          <>
            <div className="text-lg font-medium text-gray-900">Add Signature Style</div>
            <div className="text-sm text-gray-500 mt-[8px]">Add your signature style</div>
          </>
        }
        trigger={
          <Button variant="outline">
            <AddIcon color="#98A2B3" />
            Add Styles
          </Button>
        }
        onCancel={onClose}
        onConfirm={handleConfirm}
        confirmButtonProps={{
          loading,
          variant: 'primary'
        }}
      >
        <div className="mt-[20px]">
          <Tabs
            value={activityKey}
            onChange={(k) => setActivityKey(k as SignatureStyleTypeEnum)}
            items={[
              {
                key: SignatureStyleTypeEnum.TYPE,
                label: 'Type',
                children: <SignInputTab value={textSign} onChange={setTextSign} address={user?.displayAddress} />
              },
              {
                key: SignatureStyleTypeEnum.DRAW,
                label: 'Draw',
                children: (
                  <SignatureCanvas
                    canvasProps={{
                      className: 'w-full h-[190px]'
                    }}
                    className={'w-full h-[262px]'}
                    ref={ref}
                    extra={
                      <>
                        {!isBegin && (
                          <div className="absolute text-sm text-gray-600 text-center top-[40%] left-[43%]">
                            Sign Here
                          </div>
                        )}
                        <div className="text-xs text-gray-600 text-center w-[430px] break-all">
                          {user?.displayAddress}
                        </div>
                      </>
                    }
                    onBeginStroke={() => {
                      if (!isBegin) {
                        setIsBegin(true);
                      }
                    }}
                    onEndStroke={() => {
                      if (isBegin) {
                        setIsBegin(false);
                      }
                    }}
                  />
                )
              },
              {
                key: SignatureStyleTypeEnum.UPLOAD,
                label: 'Upload',
                children: <ImageUploadWithCropper ref={copperRef} />
              }
            ]}
          />
        </div>
      </Modal>
    </>
  );
};

const RemoveSignatureModal = ({ id, onSuccess }: { id: string; onSuccess: () => void }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { mutateAsync } = useMutation({
    mutationFn: deleteSignatureStyle,
    mutationKey: ['deleteSignatureStyle']
  });
  const handleConfirm = async () => {
    console.log('confirm');
    try {
      setLoading(true);
      await mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Signature style deleted',
        variant: 'success'
      });
      onSuccess();
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Modal
        open={open}
        onOpenChange={setOpen}
        trigger={<div className="cursor-pointer text-sm text-gray-500 font-medium">Delete</div>}
        onCancel={() => setOpen(false)}
        onConfirm={handleConfirm}
        confirmText={'Delete'}
        confirmButtonProps={{
          loading
        }}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorRemoveIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">Remove Style</div>
          <div className="text-sm text-gray-500 mt-[8px] text-center">
            Are you sure you want to delete this signature style? This action can not be undone.
          </div>
        </div>
      </Modal>
    </>
  );
};

interface ISignatureStyles {
  image: string;
  createdAt: string;
  id: string;
}

export const SignatureStyles = () => {
  const { user } = useUserInfo();
  const { data, refetch, isLoading } = useQuery({
    queryKey: ['signature-styles'],
    queryFn: fetchSignatureStyles
  });
  const columns: ColumnDef<ISignatureStyles>[] = [
    {
      header: 'Signature Style',
      accessorKey: 'image',
      cell: ({ row }) => {
        const record = row.original;
        return <SignPreview src={record.image} address={user?.displayAddress} />;
      }
    },
    {
      header: 'Last Used',
      accessorKey: 'createdAt',
      cell: ({ row }) => {
        return dateFormat(row.original.createdAt);
      }
    },
    {
      header: 'Actions',
      cell: ({ row }) => {
        const record = row.original;
        return <RemoveSignatureModal id={record.id} onSuccess={refetch} />;
      }
    }
  ];

  return (
    <Card
      title="Signature Styles"
      subTitle="Your saved signature styles"
      extra={<AddOrEditSignatureModal onSuccess={refetch} />}
      className={'h-full'}
    >
      <ScrollArea className="h-[calc(100%-90px)]">
        <Spin loading={isLoading}>
          <Table
            data={data?.rows || []}
            columns={columns}
            emptyText={
              <div className="py-8 text-sm">
                <div className="text-gray-900 font-medium text-md mt-[16px]">There&apos;s nothing here 😴</div>
                <div className="text-gray-500 text-sm mt-[4px] text-center">Add your first signature now!</div>
              </div>
            }
          />
        </Spin>
      </ScrollArea>
    </Card>
  );
};
